import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Search, CreditCard, Check, X, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchPayments();
  }, [page, statusFilter]);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', '20');
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      const response = await axios.get(`${API}/admin/payments?${params.toString()}`, { withCredentials: true });
      setPayments(response.data.payments);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error('Błąd podczas pobierania płatności');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <Check className="w-4 h-4 text-emerald-500" />;
      case 'failed': return <X className="w-4 h-4 text-red-500" />;
      default: return <Clock className="w-4 h-4 text-amber-500" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed': return <Badge className="bg-emerald-500">Zakończona</Badge>;
      case 'failed': return <Badge variant="destructive">Nieudana</Badge>;
      case 'pending': return <Badge className="bg-amber-500">Oczekująca</Badge>;
      default: return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculate totals
  const totalRevenue = payments
    .filter(p => p.status === 'completed')
    .reduce((sum, p) => sum + p.amount, 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Płatności</h1>
          <p className="text-slate-500">Historia transakcji ({total} płatności)</p>
        </div>
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="p-4">
            <p className="text-sm text-emerald-600">Przychód (ta strona)</p>
            <p className="text-2xl font-bold text-emerald-700">{totalRevenue.toFixed(2)} PLN</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Wszystkie</SelectItem>
                <SelectItem value="completed">Zakończone</SelectItem>
                <SelectItem value="pending">Oczekujące</SelectItem>
                <SelectItem value="failed">Nieudane</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Payments Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
            </div>
          ) : payments.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-500">
              <CreditCard className="w-12 h-12 mb-4 text-slate-300" />
              <p>Brak płatności</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-slate-500">ID</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-500">Firma</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-500">Pakiet</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-500">Kwota</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-500">Provider</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-500">Status</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-500">Data</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => (
                    <motion.tr
                      key={payment.payment_id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="border-b last:border-0 hover:bg-slate-50"
                    >
                      <td className="p-4">
                        <code className="text-xs bg-slate-100 px-2 py-1 rounded">
                          {payment.payment_id.slice(0, 12)}...
                        </code>
                      </td>
                      <td className="p-4">
                        <p className="text-sm font-medium">{payment.metadata?.business_name || '-'}</p>
                        <p className="text-xs text-slate-400">{payment.business_id}</p>
                      </td>
                      <td className="p-4">
                        <Badge className="bg-amber-500 capitalize">{payment.package_tier}</Badge>
                      </td>
                      <td className="p-4">
                        <span className="font-semibold">{payment.amount} {payment.currency}</span>
                      </td>
                      <td className="p-4">
                        <Badge variant="outline" className="capitalize">{payment.provider}</Badge>
                      </td>
                      <td className="p-4">{getStatusBadge(payment.status)}</td>
                      <td className="p-4 text-sm text-slate-500">
                        {formatDate(payment.created_at)}
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {total > 20 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Poprzednia
          </Button>
          <span className="text-sm text-slate-500">
            Strona {page} z {Math.ceil(total / 20)}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => p + 1)}
            disabled={page >= Math.ceil(total / 20)}
          >
            Następna
          </Button>
        </div>
      )}
    </div>
  );
}
